import { useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';

import THEME from '../../constants/theme';

/**
 * VideoModal component - Full-screen video player modal
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {function} props.onClose - Callback when modal closes
 * @param {string} props.videoUrl - URL of the video to play
 * @returns {React.Component} VideoModal component
 */
const VideoModal = ({ open, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  // Reset loading state when modal opens/closes
  useEffect(() => {
    if (open) {
      // Try to play video when modal opens
      if (videoRef.current) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay prevented by browser
          });
        }
      }
    } else {
      // Pause video when modal closes
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [open]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xl'
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: THEME.COLORS.NEUTRAL_BLACK,
          width: '95vw',
          height: '95vh',
          maxHeight: '95vh',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.95)'
        }
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <video
          ref={videoRef}
          controls
          autoPlay
          loop
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        >
          <source src={videoUrl} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
